import React, { useState, useEffect, useRef } from 'react';
import api from '../../config/axios';
import { useAlert } from '../../contexts/AlertContext';
import { useButtonStyles } from '../../utils/buttonStyles';

export const compareDepartmentsBySortOrder = (a, b) => {
  const aOrder = a?.sortOrder;
  const bOrder = b?.sortOrder;
  const aHasOrder = aOrder != null && aOrder !== '';
  const bHasOrder = bOrder != null && bOrder !== '';
  if (aHasOrder && bHasOrder && Number(aOrder) !== Number(bOrder)) {
    return Number(aOrder) - Number(bOrder);
  }
  if (aHasOrder !== bHasOrder) {
    return aHasOrder ? -1 : 1;
  }
  const codeA = a?.departmentCode || '';
  const codeB = b?.departmentCode || '';
  if (/^\d+$/.test(codeA) && /^\d+$/.test(codeB)) {
    return parseInt(codeA, 10) - parseInt(codeB, 10);
  }
  return codeA.localeCompare(codeB, 'ko');
};

const DepartmentTree = ({
  selectedDepartmentId,
  selectedDepartmentIds = [],
  onSelectDepartment,
  onToggleDepartment,
  onSelectAllDepartments,
  onClearDepartmentSelection,
  divisionId = null, // 사업장 ID (선택사항, 있으면 해당 사업장의 부서만 조회)
  rootDepartmentId = null, // 루트 부서 ID (있으면 해당 부서와 하위 부서만 표시)
  includeDescendants = true, // false면 루트 부서만 표시
  departments: externalDepartments = null,
  onRefresh = null,
  multiSelect = false,
  showExpandControls = false,
  showSelectionControls = false,
  showStatus = false,
  showTitle = true,
  showRefresh = true,
  initialExpanded = false,
  headerClassName = '',
  headerLeading = null,
  maxHeightClassName = 'max-h-[400px]',
  disabledDepartmentIds = [],
  disabledDepartmentTitles = null
}) => {
  const buttonStyles = useButtonStyles();
  const { showAlert } = useAlert();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const disabledIds = new Set((disabledDepartmentIds || []).map((id) => Number(id)));

  const filterDepartmentsByRoot = (items) => {
    if (!rootDepartmentId) return items;
    const root = items.find((dept) => Number(dept.id) === Number(rootDepartmentId));
    if (!root) return [];
    const rootPath = root.departmentPath;
    if (!rootPath) {
      return items.filter((dept) => Number(dept.id) === Number(rootDepartmentId));
    }
    return items.filter((dept) => (
      Number(dept.id) === Number(rootDepartmentId) ||
      (includeDescendants && dept.departmentPath && dept.departmentPath.startsWith(`${rootPath}.`))
    ));
  };

  // 활성화된 부서 목록 조회
  const fetchDepartments = async () => {
    if (externalDepartments) {
      if (onRefresh) {
        onRefresh();
      }
      setDepartments(filterDepartmentsByRoot(externalDepartments));
      return;
    }

    try {
      setLoading(true);
      const url = divisionId
        ? `/api/users/departments/active?divisionId=${divisionId}`
        : '/api/users/departments/active';
      const response = await api.get(url);
      if (response && response.data) {
        setDepartments(filterDepartmentsByRoot(response.data));
      }
    } catch (error) {
      console.error('부서 목록 조회 실패:', error);
      showAlert('부서 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (externalDepartments) {
      setDepartments(filterDepartmentsByRoot(externalDepartments));
      return;
    }
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- divisionId 또는 외부 부서 목록 변경 시 실행
  }, [divisionId, rootDepartmentId, externalDepartments, includeDescendants]);

  // 부서 목록을 계층 구조로 변환
  const buildTree = (departments) => {
    const departmentMap = new Map();
    const rootDepartments = [];

    // 모든 부서를 맵에 저장
    departments.forEach(dept => {
      departmentMap.set(dept.id, {
        ...dept,
        children: []
      });
    });

    // 부서를 계층 구조로 구성
    departments.forEach(dept => {
      const node = departmentMap.get(dept.id);
      if (dept.parentDepartmentId) {
        const parent = departmentMap.get(dept.parentDepartmentId);
        if (parent) {
          parent.children.push(node);
        } else {
          // 부모가 없으면 루트로 추가
          rootDepartments.push(node);
        }
      } else {
        // 부모가 없으면 루트로 추가
        rootDepartments.push(node);
      }
    });

    const sortSiblings = (nodes) => {
      nodes.sort(compareDepartmentsBySortOrder);
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          sortSiblings(node.children);
        }
      });
    };
    sortSiblings(rootDepartments);

    return rootDepartments;
  };

  const collectExpandableNodeIds = (nodes) => {
    const ids = [];
    const walk = (items) => {
      items.forEach(node => {
        if (node.children && node.children.length > 0) {
          ids.push(node.id);
          walk(node.children);
        }
      });
    };
    walk(nodes);
    return ids;
  };

  // 계층 트리 렌더링 (재귀적)
  const renderTree = (nodes, level = 0) => {
    if (!nodes || nodes.length === 0) return null;

    return nodes.map(node => {
      const isExpanded = expandedNodes.has(node.id);
      const hasChildren = node.children && node.children.length > 0;
      const isSelected = selectedDepartmentId === node.id;
      const isChecked = selectedDepartmentIds.map(Number).includes(Number(node.id));
      const isDeleted = node.status === 'DELETED';
      const isInactive = node.status === 'INACTIVE';
      const isNotActive = isDeleted || isInactive;
      const isDisabled = disabledIds.has(Number(node.id));

      return (
        <div key={node.id} className="select-none">
          <div
            className={`flex items-center gap-2 py-1.5 px-2 rounded ${
              isDisabled
                ? 'cursor-not-allowed opacity-50'
                : `cursor-pointer hover:bg-gray-100 ${
                  isSelected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''
                }`
            }`}
            style={{ paddingLeft: `${level * 20 + 8}px` }}
            title={isDisabled ? (
              disabledDepartmentTitles?.[Number(node.id)] || '상위 조직은 선택할 수 없습니다.'
            ) : undefined}
            onClick={() => {
              if (isDisabled || !onSelectDepartment) return;
              onSelectDepartment(node.id, node.departmentName);
            }}
          >
            {hasChildren && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const newExpanded = new Set(expandedNodes);
                  if (isExpanded) {
                    newExpanded.delete(node.id);
                  } else {
                    newExpanded.add(node.id);
                  }
                  setExpandedNodes(newExpanded);
                }}
                className="w-5 h-5 shrink-0 box-border flex items-center justify-center text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <span className="text-xs font-semibold">−</span>
                ) : (
                  <span className="text-xs font-semibold">+</span>
                )}
              </button>
            )}
            {!hasChildren && <span className="w-5 h-5 shrink-0" aria-hidden="true"></span>}
            {multiSelect && (
              <input
                type="checkbox"
                checked={isChecked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (onToggleDepartment) {
                    onToggleDepartment(node, e.target.checked);
                  }
                }}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            )}
            <span className={`text-sm font-medium ${isNotActive ? 'text-gray-500' : 'text-gray-900'}`}>
              {node.departmentName}
            </span>
            <span className="text-xs text-gray-500">
              ({node.displayCode || node.departmentCode})
            </span>
            {showStatus && isInactive && (
              <span className="text-xs text-yellow-800 bg-yellow-100 px-1.5 py-0.5 rounded">
                비활성
              </span>
            )}
            {showStatus && isDeleted && (
              <span className="text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                삭제됨
              </span>
            )}
          </div>
          {isExpanded && hasChildren && (
            <div>
              {renderTree(node.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const didExpandInitially = useRef(false);
  useEffect(() => {
    if (!initialExpanded || departments.length === 0 || didExpandInitially.current) {
      return;
    }
    didExpandInitially.current = true;
    setExpandedNodes(new Set(collectExpandableNodeIds(buildTree(departments))));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 목록이 채워질 때 한 번만 펼친다
  }, [initialExpanded, departments]);

  const treeData = buildTree(departments);
  const expandableNodeIds = collectExpandableNodeIds(treeData);
  const expandAllNodes = () => {
    setExpandedNodes(new Set(expandableNodeIds));
  };

  return (
    <div className="w-full">
      <div className={`mb-4 flex items-center ${showTitle || headerLeading ? 'justify-between' : 'justify-end'} ${headerClassName}`}>
        {showTitle && (
          <h3 className="text-sm font-semibold text-gray-900">부서 선택</h3>
        )}
        {headerLeading}
        <div className="flex items-center flex-wrap gap-1.5 justify-end">
          {multiSelect && showSelectionControls && departments.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => {
                  expandAllNodes();
                  if (onSelectAllDepartments) {
                    onSelectAllDepartments(departments);
                  }
                }}
                className={buttonStyles.btnSmall.blue}
              >
                전체 선택
              </button>
              <button
                type="button"
                onClick={() => {
                  expandAllNodes();
                  if (onClearDepartmentSelection) {
                    onClearDepartmentSelection();
                  }
                }}
                className={buttonStyles.btnSmall.gray}
              >
                전체 해제
              </button>
            </>
          )}
          {showExpandControls && expandableNodeIds.length > 0 && (
            <>
              <button
                type="button"
                onClick={expandAllNodes}
                className={buttonStyles.btnSmall.blue}
              >
                펼치기
              </button>
              <button
                type="button"
                onClick={() => setExpandedNodes(new Set())}
                className={buttonStyles.btnSmall.gray}
              >
                접기
              </button>
            </>
          )}
          {showRefresh && (
            <button
              type="button"
              onClick={fetchDepartments}
              className={buttonStyles.btnSmall.gray}
            >
              새로고침
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : treeData.length > 0 ? (
        <div className={`border border-gray-200 rounded-lg p-2 min-h-[200px] ${maxHeightClassName} overflow-y-auto`}>
          {renderTree(treeData)}
        </div>
      ) : (
        <div className="text-sm text-gray-500 text-center py-8">
          표시할 부서가 없습니다.
        </div>
      )}
    </div>
  );
};

export default DepartmentTree;
